// Epoch 19: Lead Generator Enhancements - Applied Filters Display Component  
// File: src/components/filters/AppliedFiltersDisplay.tsx
// Purpose: Show applied filters as removable tags

import React from 'react';
import { AppliedFiltersDisplayProps, AppliedFilter } from '../../types/filters';

export default function AppliedFiltersDisplay({ 
  filters, 
  metadata, 
  onRemoveFilter 
}: AppliedFiltersDisplayProps) {
  
  // Generate applied filters array with labels
  const getAppliedFilters = (): AppliedFilter[] => {
    const applied: AppliedFilter[] = [];
    
    // Helper to get label from metadata
    const getLabel = (category: keyof typeof metadata, value: string): string => {
      if (!metadata) return value;
      const option = metadata[category]?.find(opt => opt.value === value);
      return option ? option.label : value;
    };

    // Add filters from each category
    filters.countries.forEach(value => applied.push({
      category: 'countries',
      categoryLabel: 'Country',
      value,
      label: getLabel('countries', value)
    }));

    filters.cities.forEach(value => applied.push({
      category: 'cities', 
      categoryLabel: 'City',
      value,
      label: getLabel('cities', value)
    }));

    filters.jobLevels.forEach(value => applied.push({
      category: 'jobLevels',
      categoryLabel: 'Job Level', 
      value,
      label: getLabel('jobLevels', value)
    }));

    filters.jobFunctions.forEach(value => applied.push({
      category: 'jobFunctions',
      categoryLabel: 'Job Function',
      value, 
      label: getLabel('jobFunctions', value)
    }));

    filters.industries.forEach(value => applied.push({
      category: 'industries',
      categoryLabel: 'Industry',
      value,
      label: getLabel('industries', value)
    }));

    // Add deselected tiers (show what's NOT selected)
    const allTiers = ['1', '2', '3', '4', '5'];
    const deselectedTiers = allTiers.filter(tier => !filters.tiers.includes(tier));
    deselectedTiers.forEach(value => applied.push({
      category: 'tiers',
      categoryLabel: 'Tier Excluded',
      value,
      label: `Tier ${value}`
    }));

    return applied;
  };

  const appliedFilters = getAppliedFilters();

  if (appliedFilters.length === 0) {
    return null;
  }

  return (
    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 max-h-32 overflow-y-auto">
      <div className="text-sm font-medium text-gray-900 mb-2">Applied Filters:</div>
      <div className="flex flex-wrap gap-2">
        {appliedFilters.map((filter, index) => (
          <div
            key={`${filter.category}-${filter.value}-${index}`}
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
          >
            <span className="font-medium">{filter.categoryLabel}:</span>
            <span>{filter.label}</span>
            <button
              onClick={() => onRemoveFilter(filter.category, filter.value)}
              className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
              aria-label={`Remove ${filter.categoryLabel}: ${filter.label}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}